import { DataRole, ExprForm } from '@mode-switch/express';
import { Attr, Model, Sort } from '@mode-switch/model';
import { Query } from '@mode-switch/query';
import { BasicChannel, Encoding } from '@mode-vegas/vegas';
import { EncodingHelpers, QueryBuilder, VegasFrame } from '@mode/charts/util-vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledField } from '@mode/shared/util-switch';
import * as FieldHelpers from '../helpers/field-helpers';

/**
 * Asserts that the provided `arg` is truthy; if it is not, the provided message is thrown
 */
export function asserts(arg: unknown, message: string): asserts arg {
  if (!arg) throw new Error(`TableCalcDialog Error: ${message}`);
}

/**
 * If the field identified by `alias` resides on "columns" or "rows" then its corresponding basic channel is returned
 */
export function getBasicPositionalChannel(encoding: Encoding, alias: string): BasicChannel | undefined {
  const column = EncodingHelpers.buildBasicColumns(encoding);
  if (column.some(({ id }) => id === alias)) {
    return 'column';
  }

  const row = EncodingHelpers.buildBasicRows(encoding);
  if (row.some(({ id }) => id === alias)) {
    return 'row';
  }
}

/**
 * Returns an encoding pared-down to the first series that includes the provided
 * alias. For encodings with a mark channel of "*" the encoding is returned as-is.
 */
export function makeEncodingForField(encoding: Encoding, alias: string): Encoding {
  if (encoding.marks.channel === '*') {
    return encoding;
  }

  // Find the first series that includes this field
  const firstSeries = encoding.marks.series.find((series) =>
    EncodingHelpers.collectEncFields(encoding, series.field).some((f) => {
      return f.id === alias;
    })
  );

  // Remove all the other series
  const batch = EncodingHelpers.batchEncoding(encoding);
  for (const series of encoding.marks.series) {
    if (series.field !== firstSeries?.field) {
      batch.removeField(series.field);
    }
  }

  // Collect the common fields of the original encoding ..
  const commonFields = EncodingHelpers.getCommonFieldsInScopeOfField(encoding, alias);
  // Collect the remaining fields of the batch encoding ..
  const allFields = EncodingHelpers.collectEncFields(batch.complete().encoding);

  // Remove fields from the batch that are not in the common set.
  // This may result in a change to the mark channel but that's
  // fine.. we just need an encoding that keeps the common fields
  // (by-formula) on their original channel
  for (const field of allFields) {
    if (!commonFields.some((f) => f.id === field.id)) {
      batch.removeField(field.id as string);
    }
  }

  const summary = batch.complete();
  return summary.encoding;
}

/**
 * Returns a set of [discrete] field formulas that have more than one possible sort
 * configuration. This can happen if a discrete field occurs in the encoding more than
 * once and one of the fields has a nested sort applied. While we only allow one sort
 * to be applied at a time per-formula, we apply an implicit sort of data-asc on fields
 * that are not explicitly ordered.. In this scenario, the field without the nested sort
 * applied still needs to be ordered deterministically and as such has a sort of data-asc.
 * When choosing dimensions for the addressing/partitioning sets we need to indicate which
 * sort will be applied in this case; we've chosen the option with the highest specificity,
 * the nested sort
 */
export function getFormulasWithSortWarnings(query: Query.Pivot<DataRole>): Set<string> {
  const nestedSorts = (query.sorts ?? []).filter((sort) => {
    return sort.type === Sort.Type.ByNested;
  });
  if (nestedSorts.length === 0) {
    return new Set();
  }

  const dimensions = getNonMarkDims(query);
  const result = new Set<string>();

  for (const sort of nestedSorts) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, f2] = dimensions.filter((field) => {
      return sort.field.formula.source === field.formula.source && field.type === Attr.Type.Discrete;
    });
    if (f2 !== undefined) {
      result.add(f2.formula.source);
    }
  }

  return result;
}

/**
 * @description Returns the positional/facet dimensions of the query, excluding Measure Names
 * @private
 */
function getNonMarkDims(query: Query.Pivot<DataRole>): FlamingoTypes.ModeField[] {
  const channels = [query.column, query.x, query.row, query.y];
  return channels.flat().filter((field): field is FlamingoTypes.ModeField => {
    return field?.role === Attr.Role.Dimension && field.source === Attr.Source.Field;
  });
}

/**
 * Builds and returns the encoding's corresponding Pivot Query.. this is the same query
 * we generate when building plans to send to Flamingo
 */
export function makePivotQuery(view: FlamingoTypes.ModeView, encoding: Encoding): Query.Pivot<DataRole> {
  const model = Model.make(view.name, { views: [view] });
  const pivotPlan = QueryBuilder.planPivotQuery(model, view.name, encoding);
  const pivotQuery = pivotPlan.queries[0];
  asserts(pivotQuery?.type === Query.Type.Pivot, `expected a Pivot Query, saw ${pivotQuery?.type}`);
  return pivotQuery;
}

export type OutOfScopeField = {
  displayName: string;
  formula: string;
  error: string;
};

/**
 * Returns an array of {@link OutOfScopeField} results representing a custom
 * frame's invalid addressing dimensions. An addressing dimension is considered
 * invalid if it cannot be found within the provided encoding (by formula)
 */
export function getOutOfScopeAddrFields(
  view: FlamingoTypes.ModeView,
  encoding: Encoding,
  formulas: string[]
): OutOfScopeField[] {
  const inScope = EncodingHelpers.collectEncFields(encoding);
  const outOfScope = formulas.filter((formula) =>
    inScope.every((field) => {
      return field.formula !== formula;
    })
  );
  return outOfScope.map((formula) => {
    const { displayName } = CompiledField.fromFormula(view, formula, Attr.Type.Discrete);
    return {
      displayName,
      formula,
      error:
        `The table calculation is missing this required field. To fix this, ` +
        `add "${displayName}" to the visualization.`,
    };
  });
}

///
/// Menus

export type QuickCalcOpt = FieldHelpers.IOpt<ExprForm>;
export type RelativeToOpt = FieldHelpers.IOpt<ExprForm.RelativeTo>;
export type TotalUsingOpt = FieldHelpers.IOpt<ExprForm.TotalUsing>;
export type FrameTypeOpt = FieldHelpers.IOpt<VegasFrame.Type>;

export const quickCalcOptions: ReadonlyArray<QuickCalcOpt> = [
  { name: 'Difference', value: ExprForm.Difference },
  { name: 'Percent difference', value: ExprForm.PctDifference },
  { name: 'Percent of total', value: ExprForm.PctOfTotal },
];

export const relativeToOptions: ReadonlyArray<RelativeToOpt> = [
  { name: 'Previous value', value: ExprForm.RelativeTo.Prev },
  { name: 'Next value', value: ExprForm.RelativeTo.Next },
  { name: 'First value', value: ExprForm.RelativeTo.First },
  { name: 'Last value', value: ExprForm.RelativeTo.Last },
];

export const totalUsingOptions: ReadonlyArray<TotalUsingOpt> = [
  { name: 'Total', value: ExprForm.TotalUsing.Total },
  { name: 'Sum', value: ExprForm.TotalUsing.Sum },
  { name: 'Average', value: ExprForm.TotalUsing.Avg },
  { name: 'Maximum', value: ExprForm.TotalUsing.Max },
  { name: 'Minimum', value: ExprForm.TotalUsing.Min },
];

export const frameTypeOptions: ReadonlyArray<FrameTypeOpt> = [
  { name: 'Table across', value: VegasFrame.Type.TableAcross },
  { name: 'Table down', value: VegasFrame.Type.TableDown },
  { name: 'Table across-down', value: VegasFrame.Type.TableAcrossDown },
  { name: 'Table down-across', value: VegasFrame.Type.TableDownAcross },
  { name: 'Pane across', value: VegasFrame.Type.PaneAcross },
  { name: 'Pane down', value: VegasFrame.Type.PaneDown },
  { name: 'Pane across-down', value: VegasFrame.Type.PaneAcrossDown },
  { name: 'Pane down-across', value: VegasFrame.Type.PaneDownAcross },
  { name: 'Cell', value: VegasFrame.Type.Cell },
  { name: 'Custom order', value: VegasFrame.Type.Custom },
];
